import { Injectable } from '@angular/core';
import { Property, PropertyType, ComplianceStatus, PropertyDocumentType } from '@shared/definitions/property';
import { DocumentType } from '@shared/definitions/document';
import { PropertyTypeFactory } from '@core/property-types/property-type-factory';
import {
  ScoringCharacteristics} from '@core/services/property-analysis/property-scoring.helpers';
import {
  isResidential,
  isIndustrial,
  isLand,
  getQualityLevel,
  getMarketReadinessDescription,
  getScoreTooltip,
  getSectionTooltip,
  countSpecialFeatures,
  hasBalconyOrTerrace,
  hasDetailedComposition
} from '@core/services/property-analysis/property-scoring.helpers';
import { PropertyQualityScore, PropertySectionScore } from '@core/services/property-analysis/property-scoring.types';

export type { PropertyQualityScore, PropertySectionScore } from '@core/services/property-analysis/property-scoring.types';

/**
 * Service to calculate property listing quality scores based on Immoweb standards
 * 
 * Scoring is based on data completeness and quality for marketplace listings.
 * Higher scores indicate better visibility potential on platforms like Immoweb.
 */
@Injectable({
  providedIn: 'root'
})
export class PropertyScoringService {

  constructor(private propertyTypeFactory: PropertyTypeFactory) {}

  /**
   * Calculate comprehensive quality score for a property
   */
  calculatePropertyScore(property: Property): PropertyQualityScore {
    const sections: PropertySectionScore[] = [
      this.scoreGeneralInformation(property),
      this.scoreInteriorDetails(property),
      this.scoreExteriorFeatures(property),
      this.scoreLegalTechnical(property),
      this.scoreMultimedia(property)
    ];

    // Calculate weighted overall score
    const totalScore = sections.reduce((sum, s) => sum + s.score, 0);
    const totalMaxScore = sections.reduce((sum, s) => sum + s.maxScore, 0);
    const overallPercentage = totalMaxScore > 0 ? Math.round((totalScore / totalMaxScore) * 100) : 0;

    return {
      overallScore: totalScore,
      overallPercentage,
      sections,
      qualityLevel: getQualityLevel(overallPercentage),
      marketReadiness: getMarketReadinessDescription(overallPercentage)
    };
  }

  /**
   * Score: General Information (25 points max)
   * Critical fields for any property listing
   */
  private scoreGeneralInformation(property: Property): PropertySectionScore {
    const completed: string[] = [];
    const missing: string[] = [];
    let score = 0;
    const maxScore = 25;

    // Property name (2 points)
    if (property.name && property.name.trim().length > 0) {
      score += 2;
      completed.push('Property name');
    } else {
      missing.push('Property name');
    }

    // Property type (3 points - critical)
    if (property.type) {
      score += 3;
      completed.push('Property type');
    } else {
      missing.push('Property type');
    }

    // Complete address (5 points - critical)
    if (property.address?.line1 && property.address?.city && property.address?.zip) {
      score += 5;
      completed.push('Complete address');
    } else {
      missing.push('Complete address');
    }

    // Description (4 points - important for marketing).
    // Threshold is `>= 50` (not `> 50`) so the inline editor's "minimum 50
    // characters" hint is honest: exactly 50 chars satisfies the requirement.
    if (property.description && property.description.trim().length >= 50) {
      score += 4;
      completed.push('Property description');
    } else {
      missing.push('Property description');
    }

    // Year built (2 points)
    if (property.yearBuilt) {
      score += 2;
      completed.push('Year built');
    } else {
      missing.push('Year built');
    }

    // Property condition (3 points)
    if (property.propertyCondition) {
      score += 3;
      completed.push('Property condition');
    } else {
      missing.push('Property condition');
    }

    // Renovation info (2 points if renovated)
    if (property.isRenovated && property.renovationYear) {
      score += 2;
      completed.push('Renovation details');
    } else if (!property.isRenovated) {
      score += 2; // Not applicable = full points
      completed.push('No renovation (N/A)');
    } else {
      missing.push('Renovation year');
    }

    // Purchase price or current value (4 points)
    if (property.financials?.purchaseInfo?.price || property.financials?.currentValue) {
      score += 4;
      completed.push('Property value');
    } else {
      missing.push('Property value');
    }

    return {
      section: 'General Information',
      score,
      maxScore,
      percentage: Math.round((score / maxScore) * 100),
      missingFields: missing,
      completedFields: completed
    };
  }

  /**
   * Score: Interior Details (30 points max)
   * Room counts, sizes, and key features - ADAPTED BY PROPERTY TYPE
   */
  private scoreInteriorDetails(property: Property): PropertySectionScore {
    const completed: string[] = [];
    const missing: string[] = [];
    let score = 0;
    const maxScore = 30;
    const propertyType = property.type;

    // For Land properties, interior details don't apply
    if (isLand(propertyType)) {
      return {
        section: 'Interior Details',
        score: 30,
        maxScore: 30,
        percentage: 100,
        missingFields: [],
        completedFields: ['Interior details (N/A for land)']
      };
    }

    // Total area (5 points - critical for all property types)
    if (property.characteristics?.totalArea && property.characteristics.totalArea > 0) {
      score += 5;
      completed.push('Total area');
    } else {
      missing.push('Total area');
    }

    // ============= RESIDENTIAL-SPECIFIC CRITERIA =============
    if (isResidential(propertyType)) {
      // Number of bedrooms (4 points) - RESIDENTIAL ONLY
      if (property.characteristics?.bedrooms !== null && property.characteristics?.bedrooms !== undefined) {
        score += 4;
        completed.push('Number of bedrooms');
      } else {
        missing.push('Number of bedrooms');
      }

      // Number of bathrooms (3 points) - RESIDENTIAL ONLY
      if (property.characteristics?.bathrooms !== null && property.characteristics?.bathrooms !== undefined) {
        score += 3;
        completed.push('Number of bathrooms');
      } else {
        missing.push('Number of bathrooms');
      }

      // Room composition (6 points) - RESIDENTIAL ONLY
      const hasDetailedRooms = hasDetailedComposition(property.characteristics);
      const hasBasicRoomCounts = (
        (property.characteristics.livingRooms !== null && property.characteristics.livingRooms !== undefined && property.characteristics.livingRooms > 0) ||
        (property.characteristics.kitchens !== null && property.characteristics.kitchens !== undefined && property.characteristics.kitchens > 0)
      );
      if (hasDetailedRooms) {
        score += 6;
        completed.push(`Room composition (${property.characteristics.roomDetails!.length} rooms)`);
      } else if (hasBasicRoomCounts) {
        score += 4;
        completed.push('Room composition (basic counts)');
      } else {
        missing.push('Room composition details');
      }

      // Kitchen details (3 points) - RESIDENTIAL ONLY
      const hasKitchenRoom = property.characteristics.roomDetails?.some(r => r.roomType === 'kitchen');
      const hasKitchenCount = property.characteristics.kitchens !== null &&
        property.characteristics.kitchens !== undefined &&
        property.characteristics.kitchens > 0;
      if (hasKitchenRoom || hasKitchenCount) {
        score += 3;
        completed.push('Kitchen details');
      } else {
        missing.push('Kitchen details');
      }

      // Building systems (4 points)
      if (property.characteristics.buildingSystems) {
        const systems = property.characteristics.buildingSystems;
        if (systems.heatingType || systems.heatingConfiguration || systems.coolingType || systems.coolingConfiguration) {
          score += 4;
          completed.push('Building systems (heating/cooling)');
        } else {
          missing.push('Building systems');
        }
      } else {
        missing.push('Building systems');
      }

      // Special features (5 points - optional for residential)
      const specialFeatureCount = countSpecialFeatures(property.characteristics);

      if (specialFeatureCount > 0) {
        score += 5;
        completed.push(`Special features (${specialFeatureCount} items)`);
      } else {
        missing.push('Special features (optional)');
      }
    }

    // ============= OFFICE-SPECIFIC CRITERIA =============
    else if (propertyType === PropertyType.Office) {
      // Office rooms / Private offices (4 points)
      const hasOfficeRooms = property.characteristics?.roomDetails?.some(r =>
        r.roomType === 'office_room' || r.roomType === 'private_office'
      ) || ((property.characteristics as ScoringCharacteristics).officeRooms ?? 0) > 0;
      if (hasOfficeRooms) {
        score += 4;
        completed.push('Office rooms');
      } else {
        missing.push('Office rooms');
      }

      // Meeting rooms (3 points)
      const hasMeetingRooms = property.characteristics?.roomDetails?.some(r =>
        r.roomType === 'meeting_room' || r.roomType === 'conference_room'
      ) || ((property.characteristics as ScoringCharacteristics).meetingRooms ?? 0) > 0;
      if (hasMeetingRooms) {
        score += 3;
        completed.push('Meeting rooms');
      } else {
        missing.push('Meeting rooms');
      }

      // Open workspaces / workstations (4 points)
      const hasWorkspaces = property.characteristics?.roomDetails?.some(r =>
        r.roomType === 'open_workspace'
      ) || ((property.characteristics as ScoringCharacteristics).workstations ?? 0) > 0 || ((property.characteristics as ScoringCharacteristics).openWorkspaces ?? 0) > 0;
      if (hasWorkspaces) {
        score += 4;
        completed.push('Workspaces/workstations');
      } else {
        missing.push('Workspaces/workstations');
      }

      // Reception area (3 points - optional)
      const hasReception = property.characteristics?.roomDetails?.some(r =>
        r.roomType === 'reception'
      ) || (property.characteristics as ScoringCharacteristics)?.hasReception;
      if (hasReception) {
        score += 3;
        completed.push('Reception area');
      } else {
        missing.push('Reception area (optional)');
      }

      // IT Infrastructure / Fiber internet (4 points)
      const hasIT = (property.characteristics as ScoringCharacteristics)?.hasFiberInternet ||
                                  (property.characteristics as ScoringCharacteristics)?.networkInfrastructure ||
                    property.characteristics?.buildingSystems?.internetType;
      if (hasIT) {
        score += 4;
        completed.push('IT infrastructure');
      } else {
        missing.push('IT infrastructure');
      }

      // Building systems - HVAC (4 points)
      if (property.characteristics.buildingSystems) {
        const systems = property.characteristics.buildingSystems;
        if (systems.heatingType || systems.coolingType) {
          score += 4;
          completed.push('HVAC systems');
        } else {
          missing.push('HVAC systems');
        }
      } else {
        missing.push('HVAC systems');
      }

      // Kitchen/break room (3 points - optional)
      const hasKitchen = property.characteristics?.roomDetails?.some(r =>
        r.roomType === 'kitchen' || r.roomType === 'break_room' || r.roomType === 'kitchen_area'
      ) || (property.characteristics as ScoringCharacteristics)?.hasKitchenArea;
      if (hasKitchen) {
        score += 3;
        completed.push('Kitchen/break room');
      } else {
        missing.push('Kitchen/break room (optional)');
      }
    }

    // ============= WAREHOUSE-SPECIFIC CRITERIA =============
    else if (isIndustrial(propertyType)) {
      // Warehouse sections (5 points)
      const hasWarehouseSections = property.characteristics?.roomDetails?.some(r =>
        r.roomType === 'warehouse_section' || r.roomType === 'storage_zone'
      ) || ((property.characteristics as ScoringCharacteristics).warehouseSections ?? 0) > 0;
      if (hasWarehouseSections) {
        score += 5;
        completed.push('Warehouse sections');
      } else {
        missing.push('Warehouse sections');
      }

      // Ceiling height (4 points - critical for warehouses)
      if (((property.characteristics as ScoringCharacteristics).ceilingHeight ?? 0) > 0) {
        score += 4;
        completed.push('Ceiling height');
      } else {
        missing.push('Ceiling height');
      }

      // Loading docks (5 points)
      const hasLoadingDocks = property.characteristics?.roomDetails?.some(r =>
        r.roomType === 'loading_dock'
      ) || ((property.characteristics as ScoringCharacteristics).loadingDocks ?? 0) > 0;
      if (hasLoadingDocks) {
        score += 5;
        completed.push('Loading docks');
      } else {
        missing.push('Loading docks');
      }

      // Floor load capacity (4 points)
      if (((property.characteristics as ScoringCharacteristics).floorLoadCapacity ?? 0) > 0) {
        score += 4;
        completed.push('Floor load capacity');
      } else {
        missing.push('Floor load capacity');
      }

      // Office space within warehouse (3 points - optional)
      const hasOfficeSpace = property.characteristics?.roomDetails?.some(r =>
        r.roomType === 'office_space' || r.roomType === 'office_room'
      ) || ((property.characteristics as ScoringCharacteristics).officeSpaces ?? 0) > 0;
      if (hasOfficeSpace) {
        score += 3;
        completed.push('Office space');
      } else {
        missing.push('Office space (optional)');
      }

      // Building systems (4 points)
      if (property.characteristics.buildingSystems) {
        const systems = property.characteristics.buildingSystems;
          if (systems.heatingType || systems.coolingType || (property.characteristics as ScoringCharacteristics)?.hasFireSuppression) {
          score += 4;
          completed.push('Building systems');
        } else {
          missing.push('Building systems');
        }
      } else {
        missing.push('Building systems');
      }
    }

    // ============= RETAIL-SPECIFIC CRITERIA =============
    else if (propertyType === PropertyType.Retail) {
      // Sales floor area (5 points)
      const hasSalesFloor = property.characteristics?.roomDetails?.some(r =>
        r.roomType === 'sales_floor'
      ) || ((property.characteristics as ScoringCharacteristics).salesFloorArea ?? 0) > 0;
      if (hasSalesFloor) {
        score += 5;
        completed.push('Sales floor area');
      } else {
        missing.push('Sales floor area');
      }

      // Shop front width (4 points)
      if (((property.characteristics as ScoringCharacteristics).shopFrontWidth ?? 0) > 0) {
        score += 4;
        completed.push('Shop front width');
      } else {
        missing.push('Shop front width');
      }

      // Display windows (3 points)
      if (((property.characteristics as ScoringCharacteristics).displayWindows ?? 0) > 0 || (property.characteristics as ScoringCharacteristics)?.hasDisplayWindows) {
        score += 3;
        completed.push('Display windows');
      } else {
        missing.push('Display windows');
      }

      // Storage area (4 points)
      const hasStorage = property.characteristics?.roomDetails?.some(r =>
        r.roomType === 'storage_area'
      ) || ((property.characteristics as ScoringCharacteristics).storageArea ?? 0) > 0;
      if (hasStorage) {
        score += 4;
        completed.push('Storage area');
      } else {
        missing.push('Storage area');
      }

      // Ceiling height (3 points)
      if (((property.characteristics as ScoringCharacteristics).ceilingHeight ?? 0) > 0) {
        score += 3;
        completed.push('Ceiling height');
      } else {
        missing.push('Ceiling height');
      }

      // Building systems (4 points)
      if (property.characteristics.buildingSystems) {
        const systems = property.characteristics.buildingSystems;
        if (systems.heatingType || systems.coolingType) {
          score += 4;
          completed.push('Building systems (HVAC)');
        } else {
          missing.push('Building systems');
        }
      } else {
        missing.push('Building systems');
      }

      // POS/retail systems info (2 points - optional)
      if ((property.characteristics as ScoringCharacteristics)?.hasPOSSystem || ((property.characteristics as ScoringCharacteristics).numberOfPOSTerminals ?? 0) > 0) {
        score += 2;
        completed.push('POS systems info');
      } else {
        missing.push('POS systems info (optional)');
      }
    }

    // ============= GENERIC COMMERCIAL (fallback) =============
    else {
      // Generic commercial scoring
      if (property.characteristics.buildingSystems) {
        const systems = property.characteristics.buildingSystems;
        if (systems.heatingType || systems.heatingConfiguration || systems.coolingType || systems.coolingConfiguration) {
          score += 10;
          completed.push('Building systems');
        } else {
          missing.push('Building systems');
        }
      } else {
        missing.push('Building systems');
      }

      // Room composition for generic
      const hasDetailedRooms = hasDetailedComposition(property.characteristics);
      if (hasDetailedRooms) {
        score += 15;
        completed.push(`Space composition (${property.characteristics.roomDetails!.length} spaces)`);
      } else {
        missing.push('Space composition details');
      }
    }

    return {
      section: 'Interior Details',
      score,
      maxScore,
      percentage: Math.round((score / maxScore) * 100),
      missingFields: missing,
      completedFields: completed
    };
  }

  /**
   * Score: Exterior Features (20 points max)
   * Garden, terrace, parking, etc. - ADAPTED BY PROPERTY TYPE
   */
  private scoreExteriorFeatures(property: Property): PropertySectionScore {
    const completed: string[] = [];
    const missing: string[] = [];
    let score = 0;
    const maxScore = 20;
    const propertyType = property.type;

    // ============= LAND-SPECIFIC CRITERIA =============
    if (isLand(propertyType)) {
      // Land surface (8 points - critical for land)
      if (property.surfaces?.land && property.surfaces.land > 0) {
        score += 8;
        completed.push('Land surface');
      } else {
        missing.push('Land surface');
      }

      // Frontage width (4 points)
      if (((property.characteristics as ScoringCharacteristics).frontageWidth ?? 0) > 0) {
        score += 4;
        completed.push('Frontage width');
      } else {
        missing.push('Frontage width');
      }

      // Access road (3 points)
      if ((property.characteristics as ScoringCharacteristics)?.hasAccessRoad || (property.characteristics as ScoringCharacteristics)?.roadAccess) {
        score += 3;
        completed.push('Road access');
      } else {
        missing.push('Road access');
      }

      // Utilities availability (5 points)
      const hasUtilities = (property.characteristics as ScoringCharacteristics)?.hasWaterConnection ||
                                              (property.characteristics as ScoringCharacteristics)?.hasElectricityConnection ||
                                              (property.characteristics as ScoringCharacteristics)?.hasSewerConnection;
      if (hasUtilities) {
        score += 5;
        completed.push('Utilities availability');
      } else {
        missing.push('Utilities availability');
      }

      return {
        section: 'Exterior Features',
        score,
        maxScore,
        percentage: Math.round((score / maxScore) * 100),
        missingFields: missing,
        completedFields: completed
      };
    }

    // ============= RESIDENTIAL-SPECIFIC CRITERIA =============
    if (isResidential(propertyType)) {
      // Land surface (4 points if applicable for House/Villa)
      if (propertyType === PropertyType.House || propertyType === PropertyType.Villa) {
        if (property.surfaces?.land && property.surfaces.land > 0) {
          score += 4;
          completed.push('Land surface');
        } else {
          missing.push('Land surface');
        }
      } else {
        // N/A for apartments
        score += 4;
        completed.push('Land surface (N/A for apartment)');
      }

      // Parking spaces (5 points)
      if (property.characteristics?.parkingSpaces !== null && property.characteristics?.parkingSpaces !== undefined) {
        score += 5;
        completed.push(`Parking (${property.characteristics.parkingSpaces} spaces)`);
      } else {
        missing.push('Parking spaces');
      }

      // Garage details (3 points) - more relevant for houses
      if (propertyType === PropertyType.House || propertyType === PropertyType.Villa) {
        if (property.characteristics.garageDetails) {
          score += 3;
          completed.push('Garage details');
        } else {
          missing.push('Garage details (if applicable)');
        }
      } else {
        // N/A for apartments
        score += 3;
        completed.push('Garage (N/A for apartment)');
      }

      // Garden (3 points) - more relevant for houses
      if (propertyType === PropertyType.House || propertyType === PropertyType.Villa) {
        if (property.characteristics.gardenDetails) {
          score += 3;
          completed.push('Garden details');
        } else {
          missing.push('Garden (if applicable)');
        }
      } else {
        // Apartments may or may not have gardens
        if (property.characteristics.gardenDetails) {
          score += 3;
          completed.push('Garden details');
        } else {
          score += 3;
          completed.push('Garden (N/A for apartment)');
        }
      }

      // Balcony/Terrace (3 points) - very relevant for apartments
      if (hasBalconyOrTerrace(property.characteristics)) {
        score += 3;
        completed.push('Balcony/Terrace details');
      } else {
        missing.push('Balcony/Terrace (if applicable)');
      }

      // Building surface (2 points)
      if (property.surfaces?.buildingSurface && property.surfaces.buildingSurface > 0) {
        score += 2;
        completed.push('Building surface');
      } else {
        missing.push('Building surface');
      }
    }

    // ============= OFFICE-SPECIFIC CRITERIA =============
    else if (propertyType === PropertyType.Office) {
      // Parking spaces (8 points - very important for offices)
      if (property.characteristics?.parkingSpaces !== null && property.characteristics?.parkingSpaces !== undefined) {
        score += 8;
        completed.push(`Parking (${property.characteristics.parkingSpaces} spaces)`);
      } else {
        missing.push('Parking spaces');
      }

      // Building accessibility (4 points)
      if ((property.characteristics as ScoringCharacteristics)?.hasElevator || property.characteristics?.buildingSystems?.hasElevator) {
        score += 4;
        completed.push('Elevator access');
      } else {
        missing.push('Elevator access');
      }

      // Building surface / floor area (4 points)
      if (property.surfaces?.buildingSurface && property.surfaces.buildingSurface > 0) {
        score += 4;
        completed.push('Building surface');
      } else {
        missing.push('Building surface');
      }

      // Outdoor/terrace space (4 points - optional but nice for offices)
      if (hasBalconyOrTerrace(property.characteristics)) {
        score += 4;
        completed.push('Terrace/outdoor space');
      } else {
        missing.push('Terrace/outdoor space (optional)');
      }
    }

    // ============= WAREHOUSE-SPECIFIC CRITERIA =============
    else if (isIndustrial(propertyType)) {
      // Land surface (6 points - important for warehouses)
      if (property.surfaces?.land && property.surfaces.land > 0) {
        score += 6;
        completed.push('Land surface');
      } else {
        missing.push('Land surface');
      }

      // Truck parking / yard space (6 points)
      if (property.characteristics?.parkingSpaces !== null && property.characteristics?.parkingSpaces !== undefined) {
        score += 6;
        completed.push(`Parking/yard (${property.characteristics.parkingSpaces} spaces)`);
      } else {
        missing.push('Parking/yard spaces');
      }

      // Building surface (4 points)
      if (property.surfaces?.buildingSurface && property.surfaces.buildingSurface > 0) {
        score += 4;
        completed.push('Building surface');
      } else {
        missing.push('Building surface');
      }

      // Fenced/secured perimeter (4 points - optional)
      if ((property.characteristics as ScoringCharacteristics)?.hasFencedPerimeter || (property.characteristics as ScoringCharacteristics)?.hasSecurityPerimeter) {
        score += 4;
        completed.push('Secured perimeter');
      } else {
        missing.push('Secured perimeter (optional)');
      }
    }

    // ============= RETAIL-SPECIFIC CRITERIA =============
    else if (propertyType === PropertyType.Retail) {
      // Street frontage / visibility (6 points)
      if (((property.characteristics as ScoringCharacteristics).shopFrontWidth ?? 0) > 0 || ((property.characteristics as ScoringCharacteristics).streetFrontage ?? 0) > 0) {
        score += 6;
        completed.push('Street frontage');
      } else {
        missing.push('Street frontage');
      }

      // Parking availability (6 points)
      if (property.characteristics?.parkingSpaces !== null && property.characteristics?.parkingSpaces !== undefined) {
        score += 6;
        completed.push(`Parking (${property.characteristics.parkingSpaces} spaces)`);
      } else {
        missing.push('Parking availability');
      }

      // Building surface (4 points)
      if (property.surfaces?.buildingSurface && property.surfaces.buildingSurface > 0) {
        score += 4;
        completed.push('Building surface');
      } else {
        missing.push('Building surface');
      }

      // Delivery/loading access (4 points)
      if ((property.characteristics as ScoringCharacteristics)?.hasLoadingAccess || (property.characteristics as ScoringCharacteristics)?.hasDeliveryAccess) {
        score += 4;
        completed.push('Delivery/loading access');
      } else {
        missing.push('Delivery/loading access (if applicable)');
      }
    }

    // ============= GENERIC FALLBACK =============
    else {
      // Parking (5 points)
      if (property.characteristics?.parkingSpaces !== null && property.characteristics?.parkingSpaces !== undefined) {
        score += 5;
        completed.push(`Parking (${property.characteristics.parkingSpaces} spaces)`);
      } else {
        missing.push('Parking spaces');
      }

      // Land surface (5 points)
      if (property.surfaces?.land && property.surfaces.land > 0) {
        score += 5;
        completed.push('Land surface');
      } else {
        missing.push('Land surface');
      }

      // Building surface (5 points)
      if (property.surfaces?.buildingSurface && property.surfaces.buildingSurface > 0) {
        score += 5;
        completed.push('Building surface');
      } else {
        missing.push('Building surface');
      }

      // Other exterior features (5 points)
      if (property.characteristics.gardenDetails || (hasBalconyOrTerrace(property.characteristics))) {
        score += 5;
        completed.push('Outdoor features');
      } else {
        missing.push('Outdoor features (optional)');
      }
    }

    return {
      section: 'Exterior Features',
      score,
      maxScore,
      percentage: Math.round((score / maxScore) * 100),
      missingFields: missing,
      completedFields: completed
    };
  }

  /**
   * Score: Legal & Technical (15 points max)
   * Certificates, permits, inspections
   */
  private scoreLegalTechnical(property: Property): PropertySectionScore {
    const completed: string[] = [];
    const missing: string[] = [];
    let score = 0;
    const maxScore = 15;

    // PEB Certificate (5 points - very important in Belgium)
    const hasPEB = property.technical?.pebCertificates?.some(c => 
      c.status === 'active'
    ) || false;
    if (hasPEB) {
      score += 5;
      completed.push('PEB Certificate');
    } else {
      missing.push('PEB Certificate');
    }

    // Electrical inspection (3 points - mandatory)
    const hasElectrical = property.technical?.electricalInspections?.some(c => 
      c.status === 'active' && (c.complianceStatus === ComplianceStatus.Passed || c.complianceStatus === ComplianceStatus.MinorIssues)
    ) || false;
    if (hasElectrical) {
      score += 3;
      completed.push('Electrical inspection');
    } else {
      missing.push('Electrical inspection');
    }

    // Zoning information (2 points)
    if (property.legal?.zoning) {
      score += 2;
      completed.push('Zoning information');
    } else {
      missing.push('Zoning information');
    }

    // Building permits (2 points if any exist)
    const permitCount = (property.legal?.buildingPermits?.length || 0) + (property.legal?.urbanisticCertificates?.length || 0);
    if (permitCount > 0) {
      score += 2;
      completed.push(`Building permits (${permitCount})`);
    } else {
      missing.push('Building permits (if applicable)');
    }

    // Other technical compliance (3 points)
    const hasGas = property.technical?.gasInspections?.some(c => 
      c.status === 'active' && c.complianceStatus === ComplianceStatus.Passed
    ) || false;
    const hasWater = property.technical?.waterInspections?.some(c => 
      c.status === 'active' && c.complianceStatus === ComplianceStatus.Passed
    ) || false;
    
    if (hasGas || hasWater) {
      score += 3;
      completed.push('Additional technical inspections');
    } else {
      missing.push('Gas/Water inspections (if applicable)');
    }

    return {
      section: 'Legal & Technical',
      score,
      maxScore,
      percentage: Math.round((score / maxScore) * 100),
      missingFields: missing,
      completedFields: completed
    };
  }

  /**
   * Score: Multimedia (10 points max)
   * Photos and visual content
   */
  private scoreMultimedia(property: Property): PropertySectionScore {
    const completed: string[] = [];
    const missing: string[] = [];
    let score = 0;
    const maxScore = 10;

    // Count photo documents (check for both 'photo' and 'property-picture' types).
    // Document type is stored as `PropertyDocumentType` but legacy entries carry
    // `DocumentType` values — widen for the string-value comparison.
    const imageCount = property.documents?.filter(d => {
      const t = d.type as DocumentType | PropertyDocumentType;
      return t === DocumentType.Photo || t === DocumentType.PropertyPicture;
    }).length || 0;

    // Minimum images (4 points for at least 5 images)
    if (imageCount >= 5) {
      score += 4;
      completed.push(`Property images (${imageCount})`);
    } else if (imageCount > 0) {
      score += 2; // Partial credit
      completed.push(`Property images (${imageCount}, recommend 5+)`);
      missing.push(`More images needed (have ${imageCount}, recommend 5+)`);
    } else {
      missing.push('Property images (recommend 5+)');
    }

    // Good image coverage (3 points for 10+ images)
    if (imageCount >= 10) {
      score += 3;
      completed.push('Excellent image coverage');
    } else if (imageCount >= 5) {
      missing.push(`More images for better coverage (have ${imageCount}, recommend 10+)`);
    }

    // Floor plan (3 points)
    const hasFloorPlan = property.documents?.some(d => d.type === PropertyDocumentType.FloorPlan);
    if (hasFloorPlan) {
      score += 3;
      completed.push('Floor plan available');
    } else {
      missing.push('Floor plan');
    }

    return {
      section: 'Multimedia',
      score,
      maxScore,
      percentage: Math.round((score / maxScore) * 100),
      missingFields: missing,
      completedFields: completed
    };
  }

  /**
   * Get a formatted tooltip text explaining the score.
   */
  getScoreTooltip(score: PropertyQualityScore): string {
    return getScoreTooltip(score);
  }

  /**
   * Get a short tooltip for section scores.
   */
  getSectionTooltip(section: PropertySectionScore): string {
    return getSectionTooltip(section);
  }
}

